<script setup lang="ts">
import { computed, onMounted, ref, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import Icon from '@/components/partials/Icon.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import { getCountry } from '@/router/utils/getCountry'
import { getLocale } from '@/router/utils/getLocale'
import { useLocalizationStore } from '@/stores/useLocalizationStore'
import { setI18nLocale } from '@/composables/I18n'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { getSupportedCountries } from '@/config/domains/utils/getSupportedCountries'
import { getConfigurationForCountry } from '@/config/domains/utils/getConfigurationForCountry'
import { getDomainForCountry } from '@/config/domains/utils/getDomainForCountry'

const {t} = useI18n()
const {localizedRoute} = useLocalizedRouter()

const locale = getLocale()
const attrs = useAttrs()

const router = useRouter()
const route = useRoute()
const countries = getSupportedCountries()

const currentCountry = getCountry()

defineOptions({
  inheritAttrs: false,
})

withDefaults(defineProps<{
  small?: boolean,
  extramargin?: boolean
}>(), {
  small: false,
  extramargin: false,
})

const open = ref<boolean>(false)

const languages = ref<{ value: string, label: string }[]>([])

const {setCurrentLocale, setCurrentCountry} = useLocalizationStore()

async function selectLanguage(language: string) {
  if (__CAPACITOR__) {
    setCurrentLocale(language)
    setI18nLocale(language)
  } else {
    if (!route.name) {
      throw new Error('Current route is not defined')
    }

    const newRouteName: string = `${language}__${String(route.name).split('__')[1]}`
    await router.push({
      params: route.params,
      name: newRouteName,
    })

    open.value = false
  }
}

const openCountry = (country: string) => {
  const config = getConfigurationForCountry(country)

  if (__CAPACITOR__) {
    setCurrentCountry(country)

    // Set locale to default locale
    if (typeof getLocale().value !== 'string' || !config.supportedLocales.includes(<string>getLocale().value)) {
      setCurrentLocale(config.defaultLocale)
    }

    router.push(localizedRoute({name: 'home'}))
  } else {
    const domain = getDomainForCountry(country)
    const protocol = new URL(window.location.href).protocol
    const url = new URL(config.basePath, `${protocol}${domain}`)
    window.location.href = url.href
  }

  open.value = false
}

const toggle = () => {
  open.value = !open.value
}

const flag = computed(() => {
  const flag = locale.value === 'en' ? 'gb' : locale.value

  return `/assets/img/flags/${flag}.svg`
})

onMounted(() => {
  const config = getConfigurationForCountry(getCountry().value)
  config.supportedLocales.forEach((locale) => languages.value.push({
    value: locale,
    label: locale
  }))
})
</script>

<template>
  <Button @click="toggle" v-bind="attrs">
    <img :src="flag" alt="Netherlands" :class="[ 'flag', { 'flag__small': small } ]"/>
  </Button>

  <Transition>
    <div class="language-switcher" :class="[ { 'extramargin': extramargin } ]" v-show="open">
      <div class="language-switcher__wrapper">
        <div class="language-switcher__country">
          <div class="language-header">
            <span class="label">{{ t('terms.country') }}</span>
          </div>
          <div v-for="country in countries" :key="country">
            <button :class="{ 'selected': country === currentCountry }"
                    @click="openCountry(country)">
              <div class="select-option">
                <img :src="`/assets/img/flags/${country}.svg`" :alt="country" class="select-option__flag"/>
                {{ t(`countries.${country}`) }}
              </div>
              <Icon name="check" class="check" v-if="country === currentCountry"/>
            </button>
          </div>
        </div>
        <div class="language-switcher__seperator"></div>
        <div class="language-switcher__language">
          <div class="language-header">
            <span class="label">{{ t('terms.language') }}</span>
          </div>
          <div v-for="language in languages" :key="language.value">
            <button :class="{ 'selected': locale === language.value }" @click="selectLanguage(language.value)">
              <div class="select-option">
                {{ t(`languages.${language.label}`) }}
              </div>
              <Icon name="check" class="check" v-if="locale === language.value"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <div class="dark-overlay" :class="{ 'active': open }"></div>
</template>

<style scoped lang="scss">
@import "@/assets/css/variables/variables.scss";
@import "@/assets/css/base/forms.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/breakpoints-down.scss";

.button {
  &::before {
    background: #ffffff;
  }
}

.flag {
  border-radius: 9999px;
  height:        2rem;
  width:         2rem;

  &__small {
    height: 1.5rem;
    width:  1.5rem;
  }
}

@include lg-down {
  .dark-overlay {
    position:       fixed;
    top:            0;
    left:           0;
    width:          100%;
    height:         100%;
    background:     rgba(0, 0, 0, 0.3);
    z-index:        30;
    opacity:        0;
    pointer-events: none;

    &.active {
      opacity:    1;
      transition: opacity 200ms ease-in-out;
    }
  }
}

.button {
  @include lg-up {
    height: 100%;
  }

  @include lg-down {
    &:deep(.button__content) {
      padding: 0;
      border:  none;
    }
  }
}

.language-switcher {
  padding:        0.5rem;
  z-index:        1000;

  position:       absolute;
  top:            calc(60px + env(safe-area-inset-top));
  right:          5vw;
  width:          90vw;
  height:         auto;

  &.extramargin {
    top:   7rem;
    width: 96vw;
    right: 2vw;
  }

  @include lg-up {
    top:    5rem;
    right:  2rem;
    width:  auto;
    height: auto;

    &.extramargin {
      top:   8rem;
      right: 5vw;
      width: auto;
    }
  }

  background:     #ffffff;

  box-shadow:     0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  border-radius:  1rem;

  display:        flex;
  flex-direction: column;
  gap:            0.5rem;

  &__wrapper {
    display:        flex;
    flex-direction: row;
    gap:            0.5rem;
    padding:        0.5rem;
  }

  &__seperator {
    width:      1px;
    background: var(--color-neutral-primary);
    opacity:    0.1;
  }

  :deep(svg) {
    width:  1.5rem;
    height: 1.5rem;
  }

  .check {
    svg {
      fill: black;
    }
  }

  .language-header {
    margin-left:   1rem;
    margin-bottom: 0.8rem;
    font-weight:   bold;
    color:         var(--color-neutral-primary);
  }

  button {
    display:         flex;
    align-items:     center;
    justify-content: space-between;
    gap:             0.8rem;
    width:           100%;
    padding:         0.5rem 1rem;
    border:          none;
    background:      none;
    cursor:          pointer;

    @include lg-up {
      min-width: 16rem;
    }

    .select-option {
      display:     flex;
      align-items: center;
      gap:         0.5rem;

      &__flag {
        border-radius: 9999px;
        height:        1.5rem;
        width:         1.5rem;
      }
    }

    color:           var(--color-neutral-primary);
    font-weight:     normal;

    &.selected {
      background:  var(--color-neutral-light);
      font-weight: 600;
    }

    &:hover {
      background: #eeeeee;
    }
  }

  &__locale {
    position:  absolute;
    top:       50%;
    left:      50%;
    transform: translate(-50%, -50%);
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 150ms ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
